/**
 * SessionsAnalyticsWidget - Перцентильная таблица по сессиям игроков
 * ===================================================================
 *
 * Показывает перцентили (1/5/10/25/50/75/90/95/99 %) по сессиям и
 * прохождению уровней. Данные берутся из ClickHouse
 * (gamegears.prod_events_hole_alien) с учётом глобального фильтра дат.
 *
 * Дополнительно: когортный гейт по минимальному уровню — оставляет только
 * устройства, чей максимальный уровень за период >= заданного значения
 * (поле ввода, по умолчанию 10; 0 = без фильтра).
 */

import React, { useState, useEffect } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { formatDuration } from '../../utils/formatDuration';
import { withDefaultDateRange } from '../../utils/defaultDateRange';
import { completedDaysSubtitle } from '../../utils/completedDaysWindow';
import { sessions as sessionsSpecs } from '@ggstat/chart-specs';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from '../ui/table';

// Значение по умолчанию для когортного гейта по уровню (общее с MCP)
const DEFAULT_MIN_LEVEL = sessionsSpecs.analytics.defaultMinLevel;

// Колонки перцентилей: ключ в ответе + заголовок
const PERCENTILE_COLUMNS = sessionsSpecs.analytics.percentiles;

// Форматируем число с разделителями тысяч (значения уже округлены на бэкенде)
function formatNumber(value) {
  const num = Number(value) || 0;
  return num.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

// Форматируем значение ячейки по тегу формата метрики
function formatCell(value, format) {
  return format === 'duration' ? formatDuration(value) : formatNumber(value);
}

export function SessionsAnalyticsWidget() {
  // Локальное состояние гейта по уровню (страничный параметр, не глобальный фильтр)
  const [minLevel, setMinLevel] = useState(DEFAULT_MIN_LEVEL);
  const [debouncedMinLevel, setDebouncedMinLevel] = useState(DEFAULT_MIN_LEVEL);

  // Дебаунс: не дёргаем бэкенд на каждое нажатие стрелки/клавиши
  useEffect(() => {
    const t = setTimeout(() => setDebouncedMinLevel(minLevel), 400);
    return () => clearTimeout(t);
  }, [minLevel]);

  // Сервер требует диапазон дат (без него запрос — полный скан таблицы
  // событий и 400 в ответ), поэтому при пустом глобальном диапазоне
  // подставляем последние 30 полных дней.
  const widgetData = useWidgetData(
    async (filters, options) => {
      return MetricsService.getSessionsAnalytics(
        withDefaultDateRange(filters),
        debouncedMinLevel,
        options
      );
    },
    [debouncedMinLevel]
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data } = widgetProps;

  const metrics = data?.metrics || [];
  const dateRange = data?.dateRange;

  const handleMinLevelChange = (e) => {
    // Пустое поле трактуем как 0 (без фильтра); отрицательные значения отсекаем
    const raw = e.target.value;
    const next = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw)) || 0);
    setMinLevel(next);
  };

  return (
    <BaseWidget
      {...widgetProps}
      title="Перцентили сессий и прохождения уровней"
      subtitle={completedDaysSubtitle('Период', dateRange)}
      emptyMessage="Нет данных за выбранный период"
      skeletonRows={8}
    >
      <div className="mb-4 flex items-center gap-2">
        <label htmlFor="sessions-min-level" className="text-sm font-medium text-muted-foreground">
          Мин. уровень
        </label>
        <input
          id="sessions-min-level"
          type="number"
          min={0}
          step={1}
          value={minLevel}
          onChange={handleMinLevelChange}
          className="h-9 w-24 rounded-md border border-input bg-background px-3 py-1 text-sm tabular-nums shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <span className="text-xs text-muted-foreground">0 = без фильтра</span>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-left">Метрика</TableHead>
              {PERCENTILE_COLUMNS.map((col) => (
                <TableHead key={col.key} className="text-right">
                  {col.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {metrics.map((metric) => (
              <TableRow key={metric.key}>
                <TableCell className="font-medium">{metric.label}</TableCell>
                {PERCENTILE_COLUMNS.map((col) => (
                  <TableCell key={col.key} className="text-right tabular-nums">
                    {formatCell(metric.percentiles?.[col.key], metric.format)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </BaseWidget>
  );
}

export default SessionsAnalyticsWidget;
